import { Fragment, useId } from 'react';
import { Label as BaseLabel } from '@/components/label';
import { styled } from '@/styles/stitches.config';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { scaleLinear } from 'd3-scale';
import { useMemo } from 'react';
import { ChartOverlay } from './chart';
import { Point } from './scatterplot';

type D = {
  id: string;
  label: string;
};

type StripPlotProps<T extends D> = {
  axisLabel: string;
  data: T[];
  formatValue: (value: number) => string;
  getValue: (d: T) => number;
  highlightedIds?: string[];
};

const Root = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'stretch',
  width: '100%',
  paddingBlockStart: '$9',
  paddingBlockEnd: '$8',

  '@portrait': {
    paddingBlockStart: '$9',
    paddingBlockEnd: '$9',
  },

  '@bp4': {
    paddingBlockStart: '$7',
    paddingBlockEnd: '$7',
  },
});

const Centered = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const ChartRoot = styled('div', {
  position: 'relative',
  flexGrow: '1',
  display: 'flex',
  justifyContent: 'center',
  overflow: 'hidden',
});

const Svg = styled('svg', {
  position: 'absolute',
  height: '100%',
  width: 'auto',
});

const AxisLabel = styled(BaseLabel, {
  fontWeight: '$medium',
  display: 'inline-block',
  maxWidth: '40ch',
  textAlign: 'center',
  lineHeight: '$2',
  color: 'var(--color-text-light)',
});

const RightLabel = styled(BaseLabel, {
  position: 'absolute',
  transform: 'translate(0, -50%)',
  color: 'var(--color-text-light)',
  left: 'calc(50% + 4%)',
});

const LeftLabel = styled(RightLabel, {
  textAlign: 'end',
  transform: 'translate(-100%, -50%)',
  color: 'var(--color-text-light)',
  left: 'calc(50% - 4%)',
  whiteSpace: 'normal',
  maxWidth: '10rem',
});

export function StripPlot<T extends D>(props: StripPlotProps<T>) {
  const { axisLabel, data, formatValue, getValue, highlightedIds } = props;

  const formatPercentage = useNumberFormatting('percent');

  const circleMaskId = useId();
  const lineMaskId = useId();

  const r = 2;
  const y = scaleLinear().domain([0, 1]).range([100, 0]);

  const highlightedData: T[] = useMemo(() => {
    if (highlightedIds) {
      return data.filter((d) => highlightedIds.includes(d.id));
    } else {
      const sortedData = [...data].sort((a, b) => getValue(a) - getValue(b));
      const lowest = sortedData[0];
      const highest = sortedData[sortedData.length - 1];
      return [lowest, highest];
    }
  }, [data, getValue, highlightedIds]);

  const getOpacity = (id: T['id']) => {
    if (highlightedData) {
      return highlightedData.some((el) => el.id === id) ? 1 : 0.4;
    }

    return 1;
  };

  return (
    <Root>
      <Centered>
        <AxisLabel
          css={{
            marginBlockEnd: '$4',
          }}
        >
          {axisLabel}
        </AxisLabel>
        <AxisLabel
          css={{
            marginBlockEnd: '$1',
          }}
        >
          {formatPercentage(1)}
        </AxisLabel>
      </Centered>

      <ChartRoot>
        <Svg viewBox="0 0 100 100">
          <defs>
            {/* Simulate strokes using a mask. Ensures that strokes will have background color */}
            <mask id={circleMaskId}>
              {data.map((d) => (
                <circle
                  key={'mask-' + circleMaskId + '-' + d.id}
                  r={r}
                  cx={50}
                  cy={y(getValue(d))}
                  fill="white"
                  stroke="black"
                  strokeWidth="0.3"
                />
              ))}
            </mask>

            <mask id={lineMaskId}>
              <rect x="0" y="0" width="100" height="100" fill="white" />
              {highlightedData.map((d) => (
                <circle
                  key={'mask-' + circleMaskId + '-' + d.id}
                  r={r}
                  cx={50}
                  cy={y(getValue(d))}
                  fill="black"
                  stroke="black"
                  strokeWidth="0.3"
                />
              ))}
            </mask>
          </defs>

          <g mask={`url(#${lineMaskId})`}>
            <rect x="49.675" y="0" height="100" width="0.75" fill={'var(--color-text-light)'} />
          </g>

          <g mask={`url(#${circleMaskId})`}>
            {data.map((d) => (
              <Point
                key={d.id}
                r={r}
                cx="50"
                cy={y(getValue(d))}
                css={{ opacity: getOpacity(d.id) }}
              />
            ))}
          </g>

          {highlightedData && (
            <g mask={`url(#${lineMaskId})`}>
              {highlightedData.map((d) => (
                <Point
                  key={d.id}
                  r={r}
                  cx={50}
                  cy={y(getValue(d))}
                  css={{ opacity: getOpacity(d.id) }}
                  stroke="transparent"
                  strokeWidth="0.3"
                />
              ))}
            </g>
          )}
        </Svg>

        <ChartOverlay>
          {highlightedData &&
            highlightedData.map((d) => (
              <Fragment key={d.id}>
                <LeftLabel css={{ top: y(getValue(d)) + '%' }}>{d.label}</LeftLabel>
                <RightLabel css={{ top: y(getValue(d)) + '%' }}>
                  {formatValue(getValue(d))}
                </RightLabel>
              </Fragment>
            ))}
        </ChartOverlay>
      </ChartRoot>

      <Centered>
        <AxisLabel css={{ marginBlockStart: '$1' }}>{formatValue(0)}</AxisLabel>
      </Centered>
    </Root>
  );
}
